import { useEffect, useMemo, useState } from 'react'

import clsx from 'clsx'

import { RealtyImage } from 'shared/api/kyrgyzstan/search'
import GhostImage from 'shared/images/ghost.png'
import { ImageOptimized } from 'shared/ui/image-optimized'
import { ScrollSnapSlider } from 'shared/ui/scroll-snap-slider'

import s from './styles.module.scss'

const THUMBNAIL_SLIDE_WIDTH = 88
const THUMBNAIL_GAP = 8

interface ImageGallerySliderProps {
  images: RealtyImage[]
  initialIndex: number
}

const clampIndex = (index: number, maxIndex: number) => {
  if (maxIndex < 0) {
    return 0
  }

  return Math.min(Math.max(index, 0), maxIndex)
}

export const ImageGallerySlider = ({
  images,
  initialIndex,
}: ImageGallerySliderProps) => {
  const initialSlide = useMemo(
    () => clampIndex(initialIndex, images.length - 1),
    [images.length, initialIndex],
  )
  const [currentSlide, setCurrentSlide] = useState(initialSlide)

  useEffect(() => {
    setCurrentSlide(initialSlide)
  }, [initialSlide])

  const handleMainSliderChange = (index: number) => {
    setCurrentSlide(index)
  }

  const handleThumbnailClick = (index: number) => {
    setCurrentSlide(index)
  }

  return (
    <>
      <div className={s.mainSliderWrapper}>
        <ScrollSnapSlider
          className={s.mainSlider}
          trackClassName={s.mainTrack}
          slideClassName={s.slideWrapper}
          showArrows={false}
          dragMode="free"
          activeIndex={currentSlide}
          onChange={handleMainSliderChange}
          slideWidth="100%"
        >
          {images.map((image) => (
            <div
              key={image.id}
              className={s.imageContainer}
            >
              <ImageOptimized
                src={image.image_url}
                alt={String(image.id)}
                errorImage={GhostImage}
                className={s.image}
              />
            </div>
          ))}
        </ScrollSnapSlider>
      </div>

      <div className={s.thumbnailsWrapper}>
        <ScrollSnapSlider
          className={s.thumbnailSlider}
          trackClassName={s.thumbnailTrack}
          slideClassName={s.thumbnailSlide}
          showArrows={false}
          activeIndex={currentSlide}
          slideWidth={THUMBNAIL_SLIDE_WIDTH}
          gap={THUMBNAIL_GAP}
        >
          {images.map((image, index) => (
            <button
              key={image.id}
              type="button"
              className={clsx(s.thumbnailItem, {
                [s.active]: index === currentSlide,
              })}
              onClick={() => {
                handleThumbnailClick(index)
              }}
            >
              <ImageOptimized
                src={image.image_url}
                alt={String(image.id)}
                errorImage={GhostImage}
                className={s.thumbnailImage}
              />
            </button>
          ))}
        </ScrollSnapSlider>
      </div>
    </>
  )
}

export type { ImageGallerySliderProps }
